/**
 * frontend/src/pages/valuation/_useAutosave.ts
 *
 * VAL-SAVE-ON-TYPE-1A — debounced save-on-change. `save` resolves null on
 * success or the failure message. Latest wins; the payload present when
 * `enabled` turns true is the baseline and is never saved back; a pending
 * change is flushed on unmount.
 *
 * VAL-PATH-FOOTER (P2a) — `saveNow` is the footer's save-first step: it
 * cancels the debounce and sends only what is not yet stored, so a value the
 * autosave already sent is never sent twice (m3). It rejects with the failure
 * or blocked message and leaves the change unsaved.
 *
 * WALKFIX-A F10 — one save on the wire at a time. `saveNow` first waits for
 * an autosave already sent: it used to see that value as stored the moment
 * it was SENT, so Intake's run read the draft before it landed (400, "6
 * unanswered question(s)") and a second save could race the first.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { formatTimeCentral } from '@/utils/date'

import type { StepSaveResult } from './_stepContext'

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'blocked'

// WALKFIX-C F2: Central time, whatever zone the browser is in.
const savedLabel = () => `Saved · ${formatTimeCentral(new Date())}`

export function useAutosave<P>(
  payload: P,
  save: (p: P) => Promise<string | null>,
  opts: { enabled: boolean; delayMs?: number; blocked?: string | null; dirty?: boolean },
): { status: AutosaveStatus; message: string | null; saveNow: () => Promise<StepSaveResult> } {
  const { enabled, delayMs = 1500, blocked = null, dirty = false } = opts
  const [state, setState] = useState<{ status: AutosaveStatus; message: string | null }>({ status: 'idle', message: null })
  const key = JSON.stringify(payload)
  const latest = useRef({ payload, save, blocked, enabled })
  latest.current = { payload, save, blocked, enabled }
  const savedKey = useRef<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const running = useRef<Promise<string | null> | null>(null)
  const waiting = useRef(false) // saveNow is waiting on `running`; it sends what is left
  const alive = useRef(true)
  const savedAt = useRef<string | null>(null)

  const send = useCallback((nowKey: string, body: P): Promise<string | null> => {
    savedKey.current = nowKey
    let p: Promise<string | null> | null = null
    p = (async () => {
      let err: string | null
      try { err = await latest.current.save(body) } catch { err = 'Could not save.' }
      if (err && savedKey.current === nowKey) savedKey.current = null
      if (!err) savedAt.current = new Date().toISOString()
      if (running.current === p) running.current = null
      return err
    })()
    running.current = p
    return p
  }, [])

  const flush = useCallback(async (nowKey: string) => {
    timer.current = null
    if (latest.current.blocked) {
      if (alive.current) setState({ status: 'blocked', message: latest.current.blocked })
      return
    }
    if (running.current) return // the running save re-checks when it ends
    if (alive.current) setState({ status: 'saving', message: null })
    const err = await send(nowKey, latest.current.payload)
    if (alive.current) setState(err ? { status: 'error', message: err } : { status: 'saved', message: savedLabel() })
    recheck(nowKey)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // A change typed while `tried` was on the wire is sent when it ends; a
  // failed value is not retried until it changes again.
  function recheck(tried: string) {
    const next = JSON.stringify(latest.current.payload)
    if (alive.current && !waiting.current && !running.current && next !== tried && next !== savedKey.current && timer.current === null) void flush(next)
  }

  // `dirty`: the payload at enable time already holds edits the server has not
  // seen (typed before a late draft arrived), so it is not a baseline to skip.
  useEffect(() => { if (enabled && !dirty) savedKey.current = key }, [enabled]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled || key === savedKey.current) return
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => { void flush(key) }, delayMs)
  }, [key, enabled, delayMs, blocked, flush])

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
      if (timer.current) {
        clearTimeout(timer.current)
        timer.current = null
        if (!latest.current.blocked) {
          const { save: doSave, payload: last } = latest.current
          const report = (e: unknown) => console.error('Autosave on unmount failed:', e instanceof Error ? e.message : String(e))
          try { void doSave(last).catch(report) } catch (e) { report(e) }
        }
      }
    }
  }, [])

  const saveNow = useCallback(async (): Promise<StepSaveResult> => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null }
    // F10: a save already on the wire lands first; only then is "stored" true.
    waiting.current = true
    try { while (running.current) await running.current } finally { waiting.current = false }
    const { blocked: why, payload: now, enabled: on } = latest.current
    // Not started yet (nothing loaded to save against): nothing to send.
    if (!on) return { changed: false, savedAt: savedAt.current }
    // Nothing new to send is never blocked: Back on an untouched step moves.
    const nowKey = JSON.stringify(now)
    // WALKFIX-C F5: already stored by the autosave during this visit is saved, not "no changes".
    // (Covers WALKFIX-A F10: a save that landed while saveNow waited is stored this visit.)
    if (nowKey === savedKey.current) return { changed: savedAt.current !== null, savedAt: savedAt.current }
    if (why) throw new Error(why)
    const err = await send(nowKey, now)
    if (err) {
      if (alive.current) setState({ status: 'error', message: err })
      throw new Error(err)
    }
    if (alive.current) setState({ status: 'saved', message: savedLabel() })
    recheck(nowKey)
    return { changed: true, savedAt: savedAt.current }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return { ...state, saveNow }
}
